import * as React from 'react'
import { useEffect } from 'react'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Text, Loader } from '@metrisk/react_metrisk-components'

import Form from '@views/Form/Form'
import { FormField, FormStep } from '@views/Form'
import { buildFields } from '@app/utils/form'
import { getSearchParam } from '@utils/get'
import { parseSignature } from '@utils/helpers'

import { propStrategies } from './getProps'

interface IProps {
  data: any
  config: any
}

const ClaimInformationRequest: any = ({ config, data }: IProps) => {
  const [showError, setShowError] = useState(false)
  const [claimId, setClaimId] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [claimInformationRequests, setClaimInformationRequests] = useState([])
  const signature = getSearchParam('signature')

  // Maps TYPE from campaignInformationRequest table to the field
  const NORMALIZED_FIELDS: { [key: string]: string } = {
    BANK_DETAILS: 'bankDetails',
    PROOF_OF_PURCHASE: 'proofOfPurchase',
    PROOF_OF_PACKAGING: 'proofOfPackaging',
    PROOF_OF_PRODUCT: 'proofOfProduct',
    PURCHASE_INFORMATION: 'purchaseInformation',
  }

  const submit = (formData: any) => {
    data.postAdditionalDetails(claimId, signature, formData)
  }

  const getCustomProps = (fields: string[]) => {
    const props: { [key: string]: any } = {}

    for (const field of fields) {
      const claimInformationRequest = claimInformationRequests.find((missingInfo) => {
        return NORMALIZED_FIELDS[missingInfo.type] === field
      })

      const getPropsStrategy = propStrategies[field]
      if (!getPropsStrategy) continue

      const fieldProps = getPropsStrategy(claimInformationRequest)
      props[field] = fieldProps
    }

    return props
  }

  const buildFormContent = () => {
    const filteredFormFields = config.campaignConsumerFields.filter((campaignField: any) => {
      return claimInformationRequests.find((missingInfo) => {
        return NORMALIZED_FIELDS[missingInfo.type] === campaignField.field
      })
    })

    const fields = filteredFormFields.map((formField: any) => formField.field)
    const customProps = getCustomProps(fields)

    return {
      notice: () => {},
      wizard: (config: any, data: any) => ({
        onSubmit: submit,
        steps: [
          {
            id: 'step-0',
            entryPoint: true,
            submitPoint: true,
            component: FormStep,
            props: {
              submitting: data.submitting,
            },
            fieldIds: fields,
          },
        ],
        fields: buildFields(
          {
            fields: filteredFormFields,
            universe: config.campaignUniverse,
            prizes: config.campaignPrizes,
            currency: config.campaignCurrency,
            country: config.campaignCountry,
            props: {
              ...customProps,
            },
          },
          FormField
        ),
      }),
    }
  }

  const fetchInformationRequest = async (claimId: string) => {
    try {
      const response = await data.getClaimInformationRequests({
        claimId: claimId,
      })

      const informationRequests = response.data.informationRequests

      if (informationRequests.length === 0) {
        setShowError(true)
        return
      }
      setClaimInformationRequests(informationRequests)
      setShowForm(true)
    } catch (error) {
      setShowError(true)
    }
  }

  useEffect(() => {
    const parsedSignature = parseSignature(signature)
    if (!parsedSignature) {
      setShowError(true)
      return
    }
    const [claimId] = parsedSignature
    setClaimId(claimId)

    const fetchData = async () => {
      await fetchInformationRequest(claimId)
    }
    fetchData()
  }, [])

  const renderClaimInformationRequest = () => {
    if (showError) {
      return (
        <Text type={'Epsilon'} align={'Left'}>
          <FormattedMessage
            defaultMessage={
              "Your request to update information couldn't be found, please double check you've entered the correct link. If your link is definitely correct, please contact us."
            }
          />
        </Text>
      )
    }
    if (showForm) {
      return (
        <>
          <Text type={'Epsilon'} align={'Left'}>
            <FormattedMessage
              defaultMessage={
                'Please fill out all of the following incomplete fields. After completing this form you should receive an email with confirmation of your updated details.'
              }
            />
          </Text>
          <Form config={config} content={buildFormContent()} data={data} />
        </>
      )
    }
    return <Loader type={'Bounce'} />
  }

  return renderClaimInformationRequest()
}

export default ClaimInformationRequest
